<template>
  <p-container>
    <p-root-portal>
      <p-dialog v-if="completeIntegrationCallDialogVisible" :has-close-button="false" :title="$t('core.page.integrationlog.completeDialog.title')">
        <template #content>
          <p-row column>
            <p-text-field v-model="integrationStatusMessage" :label="$t('core.page.integrationlog.completeDialog.label')" required></p-text-field>
            <div>
              <p-request-alert v-model="requestError"></p-request-alert>
            </div>
          </p-row>
        </template>
        <template #actions>
          <p-button secondary @click="completeIntegrationCallDialogVisible = false">
            {{ $t('core.app.cancel') }}
          </p-button>
          <p-button :disabled="!integrationStatusMessage" @click="completeIntegrationCall()">
            {{ $t('core.app.ok') }}
          </p-button>
        </template>
      </p-dialog>
    </p-root-portal>
    <p-row column>
      <div>
        <p-row align-center>
          <p-button tertiary @click="back">
            <p-icon>
              keyboard_backspace
            </p-icon>
          </p-button>
          <h2>{{ title }}</h2>
        </p-row>
      </div>
      <p-row>
        <p-button v-if="isAdmin || isHostAdmin" primary :disabled="integrationDetail.integrationStatusId == 4 || integrationDetail.integrationStatusId == 2" @click="completeIntegrationCallPopUp()">
          <p-icon>
            warning
          </p-icon>
          &nbsp;
          {{ $t('core.page.integrationlog.page.complete') }}
        </p-button>
        <p-button v-if="isAdmin || isHostAdmin" primary :disabled="integrationDetail.integrationStatusId == 4 || integrationDetail.integrationStatusId == 2 || integrationDetail.integrationStatusId == 1 || integrationDetail.integrationStatusId == 0" @click="reExecuteIntegrationCall()">
          <p-icon>
            replay
          </p-icon>
          &nbsp;
          {{ $t('core.page.integrationlog.page.executeAgain') }}
        </p-button>
      </p-row>
      <p-col v-if="linkError">
        <p-request-alert v-model="linkError"></p-request-alert>
      </p-col>
      <p-row>
        <p-col xs12 sm12 lg6>
          <p-card class="card-detail">
            <p-progress-linear v-if="isLoading" indeterminate></p-progress-linear>
            <p-row v-else column>
              <p-col>
                <p-row column dense>
                  <div>Id: {{ id }}</div>
                  <div>{{ $t('core.page.integrationlog.list.source') }}: {{ integrationDetail.sourceAppSystemName }}</div>
                  <div>{{ $t('core.page.integrationlog.list.status') }}: {{ $tEnum('core.page.integrationlog.statusId', integrationDetail.integrationStatusId) }}</div>
                  <div>{{ $t('core.page.integrationlog.list.message') }}: {{ integrationDetail.integrationStatusMessage }}</div>
                  <div>{{ $t('core.page.integrationlog.created') }}: {{ $format.localDateAndTime(integrationDetail.created, 'L LTS') }} {{ $t('core.page.integrationlog.by') }} {{ integrationDetail.modifierName }}</div>
                  <div>{{ $t('core.page.integrationlog.list.processed') }}: {{ $format.localDateAndTime(integrationDetail.modified, 'L LTS') }}</div>
                  <div>{{ $t('core.page.integrationlog.list.runNo') }}: {{ integrationDetail.runNo }}</div>
                </p-row>
              </p-col>
            </p-row>
          </p-card>
        </p-col>
        <p-col xs12 sm12 lg6>
          <p-card class="card-detail">
            <p-row>
              <p-col>
                <div>{{ $t('core.page.integrationlog.listDetail.correlationId') }}: {{ integrationDetail.correlationId }}</div>
                <div>{{ $t('core.page.integrationlog.listDetail.appTransactionId') }}: {{ integrationDetail.appTransactionId }}</div>
                <div>{{ $t('core.page.integrationlog.listDetail.appUserId') }}: {{ integrationDetail.appUserId }}</div>
                <br>
                <router-link :to="`/_admin/callLog?correlationId=${integrationDetail.correlationId}`">
                  <p-button @click="writeRequestToQuery">
                    {{ $t('core.page.integrationlog.listDetail.toCallLogs') }}
                    &nbsp;
                    <p-icon>open_in_new</p-icon>
                  </p-button>
                </router-link>
              </p-col>
            </p-row>
          </p-card>
        </p-col>
      </p-row>
      <p-card>
        <p-progress-linear v-if="isLoading" indeterminate></p-progress-linear>
        <p-row v-else column>
          <p-accordion v-model="objectJsonExpanded">
            <template #expander="{expanded}">
              <p-row align-center>
                <h3>{{ $t('core.page.integrationlog.object') }}</h3>
                <p-icon v-if="expanded">
                  expand_less
                </p-icon>
                <p-icon v-else>
                  expand_more
                </p-icon>
              </p-row>
            </template>
            <p-code lang="json">
              {{ integrationObjectString }}
            </p-code>
          </p-accordion>
        </p-row>
      </p-card>
      <p-card>
        <p-row column>
          <p-col>
            <h3>{{ $t('core.page.integrationlog.listDetail.title') }}</h3>
          </p-col>
          <p-col>
            <p-row>
              <p-tabs v-if="integrationDetail.runNo > 1" v-model="selectedTabIndex">
                <p-tab v-for="number in integrationCallRunNumbers" :key="number" :label="'Run ' + number" @activate="loadSteps(number)"></p-tab>
              </p-tabs>
            </p-row>
            <p-table v-model="steps" :columns="columns" :loading="areStepsLoading"></p-table>
          </p-col>
        </p-row>
      </p-card>
    </p-row>
  </p-container>
</template>

<script lang="ts">
  import Vue from 'vue';
  import IntegrationCallResourceViewModel from '../../../../services/v1/viewModel/resource/IntegrationCallResourceViewModel';
  import IpTableColumn from '../../../../components/p-table/IpTableColumn';
  import CompleteIntegrationCallRequestViewModel from '../../../../services/v1/viewModel/resource/CompleteIntegrationCallRequestViewModel';
  import GetIntegrationCallStepsByCallIdRequestViewModel from '../../../../services/v1/viewModel/request/IntegrationCalls/GetIntegrationCallStepsByCallIdRequestViewModel';

  export default Vue.extend({
    auth: true,
    role: ['admin', 'hostadmin'],
    layout: 'layout-hostadmin',
    data: () => ({
      objectJsonExpanded: false,
      selectedTabIndex: Number as unknown | undefined,
      completeIntegrationCallDialogVisible: false,
      integrationStatusMessage: (undefined as string | undefined),
      requestError: undefined as any,
      columns: [
        { value: Vue.$t('core.page.integrationlog.listDetail.id'), field: 'id' },
        { value: Vue.$t('core.page.integrationlog.listDetail.nr'), field: 'number', textAlign: 'right' },
        { value: Vue.$t('core.page.integrationlog.listDetail.name'), field: 'name' },
        { value: Vue.$t('core.page.integrationlog.listDetail.target'), field: 'targetAppSystemName' },
        { value: Vue.$t('core.page.integrationlog.listDetail.applies'), field: 'isOperative' },
        { value: Vue.$t('core.page.integrationlog.listDetail.success'), field: 'isSuccess' },
        { value: Vue.$t('core.page.integrationlog.listDetail.message'), field: 'message' },
        { value: Vue.$t('core.page.integrationlog.listDetail.processed'), field: 'created', format: (value: string) => Vue.$date(value).format('DD.MM.YYYY HH:mm:ss') },
      ] as IpTableColumn[],
      isLoading: false,
      areStepsLoading: false,
      integrationDetail: {} as IntegrationCallResourceViewModel,
      linkError: (undefined as string | undefined),
      list: {},
      steps: [] as any[],
      id: Number as unknown | undefined,
      integrationCallRunNumbers: [] as any[],
    }),
    computed: {
      integrationObjectString() {
        let integrationObjectString = this.integrationDetail?.integrationObject ?? 'undefined';
        if (integrationObjectString.trim().startsWith('{')) {
          integrationObjectString = JSON.stringify(JSON.parse(integrationObjectString), undefined, 2);
        }
        return integrationObjectString;
      },
      title() {
        return this.integrationDetail.integrationTaskName ?? this.$t('core.page.integrationlog.integrationDetailTitle');
      },
      isHostAdmin() {
        return this.$auth.isInRole('HostAdmin');
      },
      isAdmin() {
        return this.$auth.isInRole('Admin');
      },
    },
    async mounted() {
      this.areStepsLoading = true;
      this.isLoading = true;
      try {
        const { id } = this.$router.currentRoute.params;
        this.id = id;
        const responseIntegrationLog = await this.$service.api.core.integrationCalls.getIntegrationCallById(id as any);
        this.integrationDetail = responseIntegrationLog;
        if (this.integrationDetail.modifierName === null || this.integrationDetail.modifierName === '') {
          this.integrationDetail.modifierName = 'None';
        }
        if (this.integrationDetail.integrationStatusMessage === null || this.integrationDetail.integrationStatusMessage === '') {
          this.integrationDetail.integrationStatusMessage = 'Success';
        }
        const req = new GetIntegrationCallStepsByCallIdRequestViewModel();
        req.runNo = this.integrationDetail.runNo;
        const responseIntegrationSteps = await this.$service.api.core.integrationCalls.getIntegrationCallStepsByCallId(id as any, req);
        this.steps = responseIntegrationSteps.items.map((e : any) => ({ ...e.toDTO() }));
        this.integrationCallRunNumbers = this.integrationCallRuns();
        this.selectedTabIndex = this.integrationDetail.runNo! - 1;
      } catch (error: any) {
        this.linkError = error;
      }
      this.isLoading = false;
      this.areStepsLoading = false;
    },
    methods: {
      back() {
        this.$router.back();
      },
      async completeIntegrationCall() {
        this.isLoading = true;
        try {
          const req = new CompleteIntegrationCallRequestViewModel();
          req.userId = this.$auth.user?.id!;
          req.comment = this.integrationStatusMessage!;
          await this.$service.api.core.integrationCalls.completeIntegrationCall(this.integrationDetail.id!, req);
          this.completeIntegrationCallDialogVisible = false;
          this.integrationDetail = await this.$service.api.core.integrationCalls.getIntegrationCallById(this.integrationDetail.id!);
        } catch (error: any) {
          this.requestError = error;
        }
        this.isLoading = false;
      },
      completeIntegrationCallPopUp() {
        this.completeIntegrationCallDialogVisible = true;
      },
      reExecuteIntegrationCall() {
        this.$router.push(`/_hostadmin/integrationlog/${this.integrationDetail.id}/reExecute`);
      },
      async loadSteps(runNo: number) {
        try {
          const req = new GetIntegrationCallStepsByCallIdRequestViewModel();
          req.runNo = runNo;
          const responseIntegrationSteps = await this.$service.api.core.integrationCalls.getIntegrationCallStepsByCallId(this.integrationDetail.id!, req);
          this.steps = responseIntegrationSteps.items.map((e : any) => ({ ...e.toDTO() }));
        } catch (error: any) {
          this.requestError = error;
        }
      },
      integrationCallRuns() {
        const numbers = [];
        const currentNumber = this.integrationDetail.runNo!;
        for (let i = currentNumber; i! > 0; i -= 1) {
          numbers.push(i);
        }
        return numbers.reverse();
      },
      writeRequestToQuery() {
        if (this.integrationDetail.correlationId != null) {
          this.$routerUtils.updateQueryParams(this.integrationDetail.toQuery(), { preserve: false });
        }
      },
    },
  });
</script>
<style scoped>
  .p-card {
    overflow-wrap: break-word;
  }
  td {
    white-space: normal !important;
  }
  .card-detail{
    height: 100%;
  }
</style>
